import {
  DialogTrigger as RACDialogTrigger,
  DialogTriggerProps,
  ModalOverlay,
  Modal as RACModal,
  composeRenderProps,
} from 'react-aria-components'
import { motion } from 'framer-motion'
import { createContext, useContext, useState, ComponentProps, Dispatch, SetStateAction } from 'react'
import { twMerge } from 'tailwind-merge'

const MotionModalOverlay = motion(ModalOverlay)
const MotionModal = motion(RACModal)

const overlayStyles =
  'fixed top-0 left-0 w-full h-[--visual-viewport-height] isolate z-200 bg-black/[40%] flex items-start justify-center p-4 text-center'
const modalStyles =
  'mt-6 w-full max-w-2xl max-h-full rounded bg-white dark:bg-zinc-800/70 dark:backdrop-blur-2xl dark:backdrop-saturate-200 forced-colors:bg-[Canvas] text-left align-middle text-gray-700 dark:text-zinc-300 shadow-2xl bg-clip-padding border border-black/10 dark:border-white/10'

export const MODAL_ANIMATION_VARIANT = {
  unmounted: 'unmounted',
  hidden: 'hidden',
  visible: 'visible',
} as const

type AnimationVariant = keyof typeof MODAL_ANIMATION_VARIANT

export const DialogTriggerAnimationStateContext = createContext<
  [AnimationVariant, Dispatch<SetStateAction<AnimationVariant>>] | null
>(null)

export function useDialogTriggerAnimations() {
  const ctx = useContext(DialogTriggerAnimationStateContext)
  if (!ctx) return {}
  const [variant, setVariant] = ctx
  return {
    isExiting: variant === MODAL_ANIMATION_VARIANT.hidden,
    onAnimationComplete: (variant: AnimationVariant) => {
      setVariant(v =>
        v === MODAL_ANIMATION_VARIANT.hidden && variant === MODAL_ANIMATION_VARIANT.hidden
          ? MODAL_ANIMATION_VARIANT.unmounted
          : v
      )
    },
    initial: MODAL_ANIMATION_VARIANT.hidden,
    animate: variant,
  }
}

export function DialogTrigger(props: DialogTriggerProps) {
  const [variant, setVariant] = useState<AnimationVariant>(MODAL_ANIMATION_VARIANT.unmounted)

  return (
    <DialogTriggerAnimationStateContext.Provider value={[variant, setVariant]}>
      <RACDialogTrigger
        onOpenChange={open => setVariant(open ? MODAL_ANIMATION_VARIANT.visible : MODAL_ANIMATION_VARIANT.hidden)}
        {...props}
      />
    </DialogTriggerAnimationStateContext.Provider>
  )
}

export type ModalProps = ComponentProps<typeof MotionModalOverlay>

export function Modal(props: ModalProps) {
  const { isOpen, onOpenChange, isDismissable, onAnimationComplete, className, ...rest } = props
  const [isExiting, setIsExiting] = useState(false)
  const animationProps = useDialogTriggerAnimations()
  return (
    <MotionModalOverlay
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      isOpen={isOpen}
      onOpenChange={
        onOpenChange &&
        (open => {
          onOpenChange(open)
          setIsExiting(!open)
        })
      }
      isDismissable={isDismissable}
      isExiting={animationProps.isExiting ?? isExiting}
      onAnimationComplete={animation => {
        const onComplete = animationProps.onAnimationComplete ?? (() => setIsExiting(false))
        onComplete(animation as AnimationVariant)
        onAnimationComplete?.(animation)
      }}
      animate={animationProps.animate ?? (isOpen ? MODAL_ANIMATION_VARIANT.visible : MODAL_ANIMATION_VARIANT.hidden)}
      initial={{ opacity: 0 }}
      {...rest}
      className={overlayStyles}
    >
      <MotionModal
        initial={{ opacity: 0, y: 32 }}
        variants={{ hidden: { opacity: 0, y: 32 }, visible: { opacity: 1, y: 0 } }}
        {...rest}
        className={composeRenderProps(className, cls => twMerge(modalStyles, cls))}
      />
    </MotionModalOverlay>
  )
}
